import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as Twilio from '@twilio/conversations';
import * as enums from '@enums';
import * as api from '@services/api';
import * as $chat from '@services/chat';
import { IChat } from '@/types';
import Messenger from './Messenger';
import { Sidebar } from './Sidebar';
import styles from './style/Messages.css';

// move onLoadUserId to query string or hash

// const mapState = (state: Store.State) => ({
//   initialized: state.chatLegacy.initialized,
//   user: state.user,
// });

export function Messages() {
  // const { initialized, user } = useSelector(mapState);
  const [sidebar, setSideBar] = useState<enums.ChatSidebar>(enums.ChatSidebar.Channels);
  const [messenger, setMessenger] = useState<IChat.DirectoryItem>();
  const [acquiring, setAcquiring] = useState<boolean>();

  const messengerActive = !!messenger;
  const activeConversationSid = messengerActive ? messenger.conversation?.state?.sid : '';

  useEffect(() => {
    const client = $chat.getClient();

    if (client) {
      const cleanupChannel = (conversation: Twilio.Conversation) => {
        if ($chat.getActiveConversation() === conversation.sid) {
          $chat.setActiveConversation(null);
          setMessenger(null);
        }
      };

      client.on('conversationRemoved', cleanupChannel);

      return () => {
        client.off('conversationRemoved', cleanupChannel);
      };
    }
  }, []);

  const setupChannel = useCallback((item: IChat.DirectoryItem) => {
    if (acquiring) return;

    if (!item.conversation) {
      setAcquiring(true);
      $chat.createConversation({
        withUserId: item.participant.contact.id,
      })
      .then(conversation => {
        $chat.setActiveConversation(conversation);
        setMessenger({
          participant: item.participant,
          conversation: {
            state: conversation,
            unreadCount: 0,
            messagesCount: 0,
            mostRecent: null,
          },
        });
        setSideBar(enums.ChatSidebar.Channels);
      })
      .finally(() => setAcquiring(false));
    } else {
      setAcquiring(true);
      const client = $chat.getClient();
      client.getConversationBySid(item.conversation.state.sid)
        .then(conversation => {
          $chat.setActiveConversation(conversation);
          conversation.setAllMessagesRead()
            .then(() => {
              if (conversation.lastMessage) {
                api.messages.consumed({
                  conversationSid: conversation.sid,
                  index: conversation.lastMessage.index,
                  timestamp: new Date(),
                });
              }
            });
          setMessenger({
            participant: item.participant,
            conversation: {
              state: conversation,
              unreadCount: 0,
              messagesCount: 0,
              mostRecent: null,
            },
          });
          setSideBar(enums.ChatSidebar.Channels);
        })
        .finally(() => setAcquiring(false));
    }
  }, [acquiring]);

  const handleSetChannel = useCallback((item: IChat.DirectoryItem) => {
    if (item) {
      setupChannel(item);
    }
  }, [setupChannel]);

  const handleToggleSidebar = useCallback((sidebar: enums.ChatSidebar) => setSideBar(sidebar), []);

  const unsetChannel = useCallback(() => {
    $chat.setActiveConversation(null);
    setMessenger(null);
  }, []);

  const deleteConversation = useCallback((conversationSid: string) => {
    $chat.deleteConversation(conversationSid)
         .then(() => unsetChannel());
  }, [unsetChannel]);

  const sendMessage = useCallback((data: { body: string; paid: boolean; }) => {
    $chat.sendMessage(data);
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <Helmet title="Messages" />
        <Sidebar
          activeConversationSid={activeConversationSid}
          messengerActive={messengerActive}
          setChannel={handleSetChannel}
          sidebar={sidebar}
          toggleView={handleToggleSidebar}
          onLoadUserId={null} />
        <Messenger
          conversation={messenger?.conversation}
          participant={messenger?.participant}
          messengerActive={messengerActive}
          onClose={unsetChannel}
          onDelete={deleteConversation}
          sendMessage={sendMessage}
          onScheduleCall={null} />
      </div>
    </div>
  );
}